import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { getStored, setStored } from "@/lib/constants";

type Theme = "light" | "dark";
export type FontSize = "small" | "normal" | "large";

const FONT_SIZES: Record<FontSize, string> = { small: "14px", normal: "16px", large: "18px" };

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  showArabic: boolean;
  setShowArabic: (show: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

function systemTheme(): Theme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Display preferences, persisted in localStorage and applied to <html>. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => getStored<Theme>("theme", systemTheme()));
  const [fontSize, setFontSize] = useState<FontSize>(() => getStored<FontSize>("font-size", "normal"));
  const [showArabic, setShowArabic] = useState<boolean>(() => getStored<boolean>("show-arabic", true));

  useEffect(() => {
    setStored("theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    setStored("font-size", fontSize);
    document.documentElement.style.fontSize = FONT_SIZES[fontSize] ?? FONT_SIZES.normal;
  }, [fontSize]);

  useEffect(() => {
    setStored("show-arabic", showArabic);
    // index.css hides [lang="ar"] content under this class.
    document.documentElement.classList.toggle("hide-arabic", !showArabic);
  }, [showArabic]);

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, isDark: theme === "dark", fontSize, setFontSize, showArabic, setShowArabic }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
